import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, LOCALE_ID, inject } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { DateUtility } from '../../date.utility';
import { DateFormatType } from '../../enums/date-format-type.enum';
import { Invoice } from '../../models/invoice-model';
import { RowLabelValue } from '../../models/row-label-value-model';
import { FormatCurrencyPipe } from '../../pipes/format-currency.pipe';
import { AppFacadeService } from '../../services/app-facade.service';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { FormatAddressPipe } from '../../shared/pipes/format-address.pipe';
import { InvoiceDueStatus, getInvoiceDueStatus } from '../invoices/renderer/invoice-number/invoice-due-status.util';

@Component({
    standalone: false,
    selector: 'por-invoices-full-width-row',
    templateUrl: './invoices-full-width-row.component.html',
    styleUrls: ['./invoices-full-width-row.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvoicesFullWidthRowComponent implements ICellRendererAngularComp {
    private readonly currencyPipe = inject(FormatCurrencyPipe);
    private readonly date = inject(DatePipe);
    private readonly featureToggleService = inject(FeatureToggleService);
    private readonly formatAddressPipe = inject(FormatAddressPipe);
    readonly appFacadeService = inject(AppFacadeService);

    public params!: ICellRendererParams;

    constructor() {
        const locale = inject(LOCALE_ID);

        this.dateFormat = DateUtility.getDateDisplayFormat(DateFormatType.StandardDate, locale);
    }

    invoice!: Invoice;
    invoiceKV: RowLabelValue[] = [];
    dueStatus: InvoiceDueStatus = null;
    dateFormat = '';
    agInit(params: ICellRendererParams): void {
        this.params = params;
        this.invoice = { ...params.data };
        this.dueStatus = getInvoiceDueStatus(this.invoice?.DueDate, this.invoice?.AmountDue);
        this.invoiceKV = [
            ...(this.featureToggleService.isAvailable('canSeeInvoiceContractIdCol')
                ? [
                      {
                          label: 'InvoiceColumnHeaderContract#',
                          value: this.invoice?.Identifiers?.ContractId ?? ''
                      }
                  ]
                : []),
            {
                label: 'invoiceNumber',
                value: this.invoice?.InvoiceNumber ?? ''
            },
            ...(this.featureToggleService.isAvailable('canSeeInvoiceAmountDueCol')
                ? [
                      {
                          label: 'InvoiceColumnHeaderAmountDue',
                          value: this.invoice?.AmountDue ? this.currencyPipe.transform(this.invoice.AmountDue) : ''
                      }
                  ]
                : []),
            ...(this.featureToggleService.isAvailable('canSeeInvoiceDepotCol')
                ? [
                      {
                          label: 'Depot',
                          value: this.invoice?.DepotName ?? ''
                      }
                  ]
                : []),
            ...(this.featureToggleService.isAvailable('canSeeInvoiceSiteCol')
                ? [
                      {
                          label: 'location',
                          value: this.invoice?.AdditionalFields?.Address ? this.formatAddressPipe.format(this.invoice.AdditionalFields.Address) : ''
                      }
                  ]
                : []),
            ...(this.featureToggleService.isAvailable('canSeeInvoiceTypeCol')
                ? [
                      {
                          label: 'Type',
                          value: this.invoice?.Type ?? ''
                      }
                  ]
                : []),
            ...(this.featureToggleService.isAvailable('canSeeInvoiceDueDateCol')
                ? [
                      {
                          label: 'InvoiceColumnHeaderDueDate',
                          value: this.invoice?.DueDate ? this.date.transform(this.invoice.DueDate, this.dateFormat) : ''
                      }
                  ]
                : []),
            ...(this.featureToggleService.isAvailable('canSeeInvoiceBilledDateCol')
                ? [
                      {
                          label: 'InvoiceColumnHeaderBilledDate',
                          value: this.invoice?.InvoiceDate ? this.date.transform(this.invoice.InvoiceDate, this.dateFormat) : ''
                      }
                  ]
                : []),
            ...(this.featureToggleService.isAvailable('canSeeInvoicePOCol')
                ? [
                      {
                          label: 'InvoiceColumnHeaderPO',
                          value: this.invoice?.CustomerPurchaseOrderNumber ?? ''
                      }
                  ]
                : []),
            ...(this.featureToggleService.isAvailable('canSeeInvoiceJobNumberCol')
                ? [
                      {
                          label: 'InvoiceColumnHeaderJobNumber',
                          value: this.invoice?.CustomerJobNumber ?? ''
                      }
                  ]
                : []),
            {
                label: 'invoiceTotal',
                value: this.invoice?.GrandTotal != null ? this.currencyPipe.transform(this.invoice.GrandTotal) : ''
            },
            ...(this.featureToggleService.isAvailable('canSeeInvoiceGrandTaxTotalCol')
                ? [
                      {
                          label: 'invoiceTax',
                          value: this.invoice?.GrandTaxTotal != null ? this.currencyPipe.transform(this.invoice.GrandTaxTotal) : ''
                      }
                  ]
                : [])
        ];
    }

    refresh() {
        return true;
    }

    loadContractDetail() {
        this.params.context?.componentParent.loadContractDetailModal(this.invoice?.Identifiers.ContractId);
    }

    loadInvoiceDetail() {
        this.params.context?.componentParent.loadInvoiceDetailModal(this.params.data);
    }

    viewInvoicePdf() {
        this.params.context?.componentParent.viewInvoicePdf(this.params.data);
    }

    onCheckboxChange(isChecked: boolean): void {
        if (this.params.context && this.params.context.componentParent) {
            this.params.context.componentParent.onFullWidthRowSelectionChanged(isChecked, this.invoice?.Id);
        }
    }
}
